import { type ReactNode } from 'react'
import { Drawer } from '@mui/material'
import { Box, useIsMobileVariant, Toolbar } from '@dhl-ui/components'
import { usePage } from '../../context'
import { MainMenu } from './MainMenu'
import { type PageMenuItem } from './types'

type Props = {
  /**
   * You can place any custom element above our Main menu
   * For example in ops/routing we have a hub selection above our side menu
   *
   * You can use our hook `usePage` to understand when we are expanded or collapsed
   * which gives you the option to make something usefull when collapsed
   */
  children?: ReactNode

  /**
   * When you need something below the main menu
   *
   * You can use our hook `usePage` to understand when we are expanded or collapsed
   * which gives you the option to make something usefull when collapsed
   */
  endNode?: ReactNode

  /**
   * Navigational items
   */
  items: PageMenuItem[]

  /**
   * Tell our consumer which item is selected so they can do their logic (mostly routing)
   */
  onSelect: (arg: PageMenuItem) => void
}

/**
 * Side menu for Page
 */
export const SideMenu = ({ children, items, endNode, onSelect }: Props) => {
  const isSmall = useIsMobileVariant()
  const { sideMenuWidth: width, isExpanded } = usePage()

  if (isSmall) {
    return (
      <Box
        sx={{
          '& .MuiList-root': {
            borderRadius: 0,
            border: 'none',
          },
        }}
      >
        <Toolbar />
        {children}
        <MainMenu items={items} endNode={endNode} onSelect={onSelect} />
      </Box>
    )
  }

  return (
    <Drawer
      open={isExpanded}
      variant="permanent"
      data-testid="page-side-menu"
      ModalProps={{
        keepMounted: true,
      }}
      slotProps={{
        paper: {
          sx: {
            width,
            zIndex: (theme) => theme.zIndex.appBar - 1,
            transition: 'width 100ms ease-in-out',
            overflowX: 'hidden',
            overflowY: 'auto',
            display: 'flex',
            displayPrint: 'none',
            border: 'none',
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        },
      }}
      sx={{
        width,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width,
        },
      }}
    >
      <Toolbar />
      {children}
      <MainMenu items={items} endNode={endNode} onSelect={onSelect} />
    </Drawer>
  )
}
